import { SlotData, SlotDayData, SlotPeriod } from '../../types/slotData';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

// This function formats the slots for display in the slot picker
export function formatSlotForSlotPicker(slots: SlotData[], locale: string): SlotDayData[] {
    const groupedSlots: Record<string, SlotDayData> = {};

    slots.forEach((slot) => {
        const date = new Date(slot.startDate);
        const dayNumber = date.getDate();
        const dayName = date.toLocaleDateString(locale, { weekday: 'short' }).replace(/\.$/, '');

        const day = date.toISOString();

        const key = `${date.getFullYear()}-${date.getMonth()}-${dayNumber}`;

        if (!groupedSlots[key]) {
            groupedSlots[key] = { dayNumber, dayName, day };
        }

        const hours = date.getHours();

        if (hours < 12 && !groupedSlots[key].firstPeriod) {
            groupedSlots[key].firstPeriod = slot;
        } else if (hours >= 12 && !groupedSlots[key].secondPeriod) {
            groupedSlots[key].secondPeriod = slot;
        }
    });

    return Object.entries(groupedSlots)
        .sort(([keyA], [keyB]) => new Date(keyA).getTime() - new Date(keyB).getTime())
        .map(([, value]) => value);
}

// This function formats a slot for display in the slot picker for interns and sign page
export function displayAndformatSlot(slot: SlotData, locale: string): string {
    const startDate = new Date(slot.startDate);
    const endDate = new Date(slot.endDate);

    const dateStr = startDate.toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' });

    const startTime = startDate.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
    const endTime = endDate.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });

    return `${dateStr} ${startTime} - ${endTime}`;
}

export function formatDateSlotForApi(data: { sessionId: string; newSlotDate?: dayjs.Dayjs }): SlotDayData[] {
    const { newSlotDate, sessionId } = data;

    if (!newSlotDate || !dayjs.isDayjs(newSlotDate)) {
        throw new Error('Invalid or missing "newSlotDate"');
    }

    const timeZone = dayjs.tz.guess();

    const day = newSlotDate.format('YYYY-MM-DD');
    const dayNumber = newSlotDate.day();
    const dayName = newSlotDate.format('dddd');

    const periods: {
        startHour: number;
        startMinute: number;
        endHour: number;
        endMinute: number;
        key: SlotPeriod;
    }[] = [
        {
            startHour: 8,
            startMinute: 30,
            endHour: 12,
            endMinute: 0,
            key: SlotPeriod.FIRST_PERIOD
        },
        {
            startHour: 13,
            startMinute: 30,
            endHour: 17,
            endMinute: 0,
            key: SlotPeriod.SECOND_PERIOD
        }
    ];

    const slotDay: SlotDayData = {
        day,
        dayNumber,
        dayName
    };

    periods.forEach(({ startHour, startMinute, endHour, endMinute, key }) => {
        const start = newSlotDate.tz(timeZone).set('hour', startHour).set('minute', startMinute).set('second', 0);
        const end = newSlotDate.tz(timeZone).set('hour', endHour).set('minute', endMinute).set('second', 0);

        const slotData: SlotData = {
            id: '',
            sessionId,
            startDate: start.format(),
            endDate: end.format()
        };

        slotDay[key] = slotData;
    });

    return [slotDay];
}
